import React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import BackButton from "@/components/common/BackButton";
import { cn } from "@/lib/utils";
import { DataField } from "@/components/pages/reservations/guest_page/components/DataField";
import {
  CircleUserRound,
  LogOut,
  MessageSquare,
  Pencil,
  Plus,
  SquareUserRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  guestKeys,
  useGuest,
} from "@/components/pages/reservations/guests/hooks/useGuests";
import { PresignedImageWithPlaceholder } from "@/components/common/PresignedImageWithPlaceholder";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { DeletionDangerZone } from "@/components/common/data-deletion/DeletionDangerZone";
import { AnonymizeGuestDialog } from "@/components/common/data-deletion/AnonymizeGuestDialog";
import { ExportActionCard } from "@/components/common/data-export/ExportActionCard";
import { useExportFlow } from "@/components/common/data-export/useExportFlow";
import { hasRole } from "@/utils/accessControl";
import { Role } from "@/types/user";
import { DeletionTargetType } from "@/types/data-deletion";
import { ExportTargetType } from "@/types/data-export";
import type { Document as GuestDocument } from "@/types/document";
import { useGuestUpdate } from "../guests/hooks/useGuestUpdate";
import { getGuestProfileNotes } from "../guests/utils/guestPreferences";
import {
  determineGuestStatus,
  type GuestStatusInput,
} from "../guests/utils/guestStatusUtils";
import { GuestFormData, guestSchema } from "../guests/utils/guestSchema";
import { useGuestVisits } from "../../settlement/hooks/useSettlement";
import GuestVisitRow from "./components/GuestVisitRow";
import { TransactionHistoryByEntity } from "../components/TransactionHistoryByEntity";
import { useCreateChannelWithGuest } from "@/components/pages/chats/hooks/useChannel";
import { useUpdateReservationStatus } from "@/hooks/useReservations";
import { CheckinStatus, CheckoutStatus } from "@/types/reservation";
import { CHECKED_OUT_GUEST_STATUS } from "../utils/reservationGuestStatusFilters";

import ISO6391 from "iso-639-1";

const getIdDocumentStatus = (documents?: GuestDocument[]) => {
  if (!documents?.length) {
    return "Not uploaded";
  }

  return documents.every((document) => document.isApproved)
    ? "Verified"
    : "Pending verification";
};

const toTime = (value: Date | string | null | undefined) => {
  const time = value ? new Date(value).getTime() : Number.NaN;
  return Number.isNaN(time) ? 0 : time;
};

const CHECKOUT_WINDOW_MS = 24 * 60 * 60 * 1000;

const isWithinCheckoutWindow = (
  departureDate: Date | string | null | undefined,
) => {
  const departureTime = toTime(departureDate);

  if (!departureTime) {
    return false;
  }

  return departureTime - Date.now() <= CHECKOUT_WINDOW_MS;
};

const getBookingMessageRank = (
  arrivalTime: number,
  departureTime: number,
  now: number,
) => {
  if (arrivalTime <= now && now <= departureTime) {
    return 0;
  }

  if (arrivalTime > now) {
    return 1;
  }

  return 2;
};

const getPreferredBookingId = (
  bookings: Array<{
    id: string;
    arrivalDate?: Date | string | null;
    departureDate?: Date | string | null;
  }>,
) => {
  const now = Date.now();

  return [...bookings].sort((a, b) => {
    const aArrival = toTime(a.arrivalDate);
    const bArrival = toTime(b.arrivalDate);
    const aDeparture = toTime(a.departureDate);
    const bDeparture = toTime(b.departureDate);

    const aRank = getBookingMessageRank(aArrival, aDeparture, now);
    const bRank = getBookingMessageRank(bArrival, bDeparture, now);

    if (aRank !== bRank) {
      return aRank - bRank;
    }

    return aRank === 2 ? bArrival - aArrival : aArrival - bArrival;
  })[0]?.id;
};

export const GuestPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { userProfile } = useAuthenticatedUser();
  const { data: guest } = useGuest(id || "");
  const { mutateAsync: createChannelWithGuest, isPending: isCreatingChannel } =
    useCreateChannelWithGuest();
  const updateReservationStatusMutation = useUpdateReservationStatus();

  const { handleGuestUpdate: updateGuest } = useGuestUpdate();
  const [imageKey, setImageKey] = React.useState(0);
  const [anonymizeOpen, setAnonymizeOpen] = React.useState(false);
  const exportFlow = useExportFlow();

  const avatarInputRef = React.useRef<HTMLInputElement | null>(null);

  const languages = React.useMemo(() => {
    const codes = ISO6391.getAllCodes();
    return codes
      .map((code) => ({
        code,
        name: ISO6391.getNativeName(code) || ISO6391.getName(code),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const renderTitle = (title: string, className?: string) => {
    return <h1 className={cn("text-center text-4xl", className)}>{title}</h1>;
  };

  const { total: guestVisitsCount, reservations: guestVisits } = useGuestVisits(
    {
      perPage: 100,
    },
    guest?.profile?.hotelId,
    id,
  );

  const selectedBookingId = React.useMemo(() => {
    const bookingIdFromUrl = searchParams.get("bookingId");

    if (bookingIdFromUrl) {
      return bookingIdFromUrl;
    }

    return getPreferredBookingId(guest?.primaryBookings ?? []);
  }, [guest?.primaryBookings, searchParams]);

  const selectedGuestBooking = React.useMemo(() => {
    if (!selectedBookingId) {
      return undefined;
    }

    return guest?.primaryBookings.find(
      (booking) => booking.id === selectedBookingId,
    );
  }, [guest?.primaryBookings, selectedBookingId]);

  const selectedGuestVisit = React.useMemo(() => {
    if (!selectedBookingId) {
      return undefined;
    }

    return guestVisits?.find((visit) => visit.id === selectedBookingId);
  }, [guestVisits, selectedBookingId]);

  const selectedBookingCheckinStatus =
    selectedGuestBooking?.checkinStatus ?? selectedGuestVisit?.checkinStatus;
  const selectedBookingCheckoutStatus =
    selectedGuestBooking?.checkoutStatus ?? selectedGuestVisit?.checkoutStatus;
  const selectedBookingPreCheckinStatus =
    selectedGuestBooking?.preCheckinStatus ??
    selectedGuestVisit?.preCheckinStatus;
  const selectedBookingPreCheckoutStatus =
    selectedGuestBooking?.preCheckoutStatus ??
    selectedGuestVisit?.preCheckoutStatus;
  const selectedBookingDepartureDate =
    selectedGuestBooking?.departureDate ?? selectedGuestVisit?.departureDate;
  const guestStatus = React.useMemo(() => {
    if (!selectedBookingId) {
      return "Not specified";
    }

    if (selectedBookingCheckoutStatus === CheckoutStatus.DONE) {
      return CHECKED_OUT_GUEST_STATUS;
    }

    return (
      determineGuestStatus({
        preCheckinStatus: selectedBookingPreCheckinStatus,
        checkinStatus: selectedBookingCheckinStatus,
        preCheckoutStatus: selectedBookingPreCheckoutStatus,
        checkoutStatus: selectedBookingCheckoutStatus,
      } as GuestStatusInput) ?? "Not specified"
    );
  }, [
    selectedBookingCheckinStatus,
    selectedBookingCheckoutStatus,
    selectedBookingId,
    selectedBookingPreCheckinStatus,
    selectedBookingPreCheckoutStatus,
  ]);
  const canCheckInSelectedBooking =
    Boolean(selectedBookingId) &&
    selectedBookingCheckinStatus === CheckinStatus.NOT_DONE;
  const canCheckOutSelectedBooking =
    Boolean(selectedBookingId) &&
    selectedBookingCheckinStatus === CheckinStatus.DONE &&
    selectedBookingCheckoutStatus === CheckoutStatus.NOT_DONE &&
    isWithinCheckoutWindow(selectedBookingDepartureDate);

  if (!guest) return null;

  const guestHotelId = guest.profile.hotelId;
  const userHotelIds = userProfile?.hotelIds;
  // HOTEL_ADMIN may anonymise AND export guests of their own hotel(s) only
  // (enforced server-side). Gate on role + hotelIds when available.
  const canManageOwnHotelGuest =
    hasRole(userProfile, Role.HOTEL_ADMIN) &&
    (userHotelIds == null ||
      guestHotelId == null ||
      userHotelIds.includes(guestHotelId));
  const guestConfirmValue = guest.profile.email?.trim() || guest.id;

  // Anonymise is async; the dialog polls the job to terminal, so don't navigate
  // away (that would unmount the poller). Refresh the now-anonymised guest on
  // completion instead.
  const handleAnonymizeCompleted = () => {
    if (id) {
      queryClient.invalidateQueries({ queryKey: guestKeys.detail(id) });
    }
    queryClient.invalidateQueries({ queryKey: guestKeys.lists() });
  };

  const handleUpdateGuestField = async ({
    field,
    value,
  }: {
    field: keyof typeof guest.profile;
    value: string | object;
  }) => {
    if (!guest) return;

    const baseData = guestSchema.parse({
      ...guest.profile,
      preferences:
        typeof guest.profile.preferences === "string"
          ? JSON.parse(guest.profile.preferences || "{}")
          : guest.profile.preferences || {},
    });

    const updatedData = {
      ...baseData,
      ...(field !== "photoId" && { [field]: value }),
    } as GuestFormData;

    await updateGuest(
      guest.id,
      updatedData,
      field === "photoId" ? guest.profile.photoId : undefined,
      field === "photoId" ? (value as File) : undefined,
    );
  };

  const noAccommodationPreferencesLabel = `“No preferences specified”`;

  const guestFullName = `${guest.profile.firstName} ${guest.profile.lastName}`;
  const parsedPreferences =
    typeof guest.profile.preferences === "string"
      ? JSON.parse(guest.profile.preferences || "{}")
      : guest.profile.preferences || {};
  const checkinComments = guest.primaryBookings
    .map((booking) => booking.checkinComments)
    .filter((comment): comment is string => Boolean(comment?.trim()));
  const accommodationPreferences =
    parsedPreferences.accommodation ||
    checkinComments.join(", ") ||
    noAccommodationPreferencesLabel;
  const guestPreferences = getGuestProfileNotes(parsedPreferences);
  const foodPreferences = parsedPreferences.food || [];
  const idDocumentStatus = getIdDocumentStatus(guest.documents);

  const prefferedLanguage = guest.profile.language ? (
    <div>
      {languages.find((l) => l.code === guest.profile.preferredLanguage!)?.name}
    </div>
  ) : (
    "Not Specified"
  );

  const openAvatarPicker = () => {
    avatarInputRef.current?.click();
  };

  const onAvatarFileChange: React.ChangeEventHandler<HTMLInputElement> = async (
    e,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    e.target.value = "";

    if (!file.type.startsWith("image/")) return;

    await handleUpdateGuestField({ field: "photoId", value: file }).then(() => {
      setImageKey((prev) => prev + 1);
    });
  };

  const handleMessageGuest = async () => {
    if (!selectedBookingId) {
      return;
    }

    const channel = await createChannelWithGuest(selectedBookingId);

    if (!channel.id) {
      return;
    }

    navigate(`/chats?activeChannel=${encodeURIComponent(channel.id)}`);
  };

  const handleCheckInGuest = async () => {
    if (!selectedBookingId) {
      return;
    }

    await updateReservationStatusMutation.mutateAsync({
      id: selectedBookingId,
      checkinStatus: CheckinStatus.DONE,
    });

    queryClient.invalidateQueries({ queryKey: guestKeys.detail(guest.id) });
  };

  const handleCheckOutGuest = async () => {
    if (!selectedBookingId) {
      return;
    }

    await updateReservationStatusMutation.mutateAsync({
      id: selectedBookingId,
      checkoutStatus: CheckoutStatus.DONE,
    });

    queryClient.invalidateQueries({ queryKey: guestKeys.detail(guest.id) });
  };

  return (
    <div className={"relative flex flex-col p-12 text-zinc-900"}>
      <BackButton className={"absolute"} />

      <div
        className={
          "mx-auto grid w-full max-w-[1040px] grid-cols-2 gap-x-4 gap-y-6"
        }
      >
        <div className="col-span-2 flex flex-col gap-3">
          {renderTitle("Guest profile")}

          <div className="flex flex-col items-center justify-center gap-2">
            <div className="relative">
              <PresignedImageWithPlaceholder
                key={imageKey}
                className={
                  "size-[72px] overflow-hidden rounded-full object-cover"
                }
                photo={guest.profile.photoId}
                placeholder={
                  <CircleUserRound className="size-[72px] overflow-hidden rounded-full stroke-1 object-cover" />
                }
              />

              <button
                type="button"
                onClick={openAvatarPicker}
                className={cn(
                  "absolute bottom-[-10px] left-[50%] translate-x-[-50%]",
                  "size-6 rounded-full border border-zinc-900 bg-white",
                  "flex items-center justify-center",
                  "cursor-pointer hover:bg-zinc-50 active:scale-[0.98]",
                )}
                aria-label="Change avatar"
                title="Change avatar"
              >
                {guest.profile.photoId ? (
                  <Pencil className="size-4" />
                ) : (
                  <Plus className="size-4" />
                )}
              </button>

              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onAvatarFileChange}
              />
            </div>

            <h2 className="text-2xl text-stone-500">{guestFullName}</h2>

            <div className="flex items-center gap-2">
              {canCheckInSelectedBooking ? (
                <Button
                  type="button"
                  variant="icon-text"
                  onClick={handleCheckInGuest}
                  disabled={updateReservationStatusMutation.isPending}
                  className="text-foreground h-8 min-h-8 gap-2 border border-slate-200 px-3 py-1 text-sm font-medium shadow-xs"
                >
                  <SquareUserRound className="size-4" />
                  {t("reservation.buttons.checkIn")}
                </Button>
              ) : null}

              {canCheckOutSelectedBooking ? (
                <Button
                  type="button"
                  variant="icon-text"
                  onClick={handleCheckOutGuest}
                  disabled={updateReservationStatusMutation.isPending}
                  className="text-foreground h-8 min-h-8 gap-2 border border-slate-200 px-3 py-1 text-sm font-medium shadow-xs"
                >
                  <LogOut className="size-4" />
                  {t("reservation.buttons.checkOut")}
                </Button>
              ) : null}

              {selectedBookingId ? (
                <Button
                  type="button"
                  variant="icon-text"
                  onClick={handleMessageGuest}
                  disabled={isCreatingChannel}
                  className="text-foreground h-8 min-h-8 gap-2 border border-slate-200 px-3 py-1 text-sm font-medium shadow-xs"
                >
                  <MessageSquare className="size-4" />
                  {t("reservation.buttons.sendMessage")}
                </Button>
              ) : null}
            </div>
          </div>
        </div>

        <div className="col-span-2 border-1 border-stone-500" />

        <DataField
          value={guest.profile.firstName || "Empty"}
          editable={true}
          onSave={(v) =>
            handleUpdateGuestField({ field: "firstName", value: v })
          }
          label={"Guest name"}
        />
        <DataField
          value={guest.profile.lastName || "Empty"}
          label={"Surname"}
          editable={true}
          onSave={(v) =>
            handleUpdateGuestField({ field: "lastName", value: v })
          }
        />

        {renderTitle("Contacts", "col-span-2 text-left text-2xl")}
        <DataField
          value={guest.profile.email}
          label={"Email"}
          editable={true}
          onSave={(v) => handleUpdateGuestField({ field: "email", value: v })}
        />
        <DataField
          value={guest.profile.phone || "Empty"}
          label={"Phone"}
          editable={true}
          onSave={(v) => handleUpdateGuestField({ field: "phone", value: v })}
        />
        <DataField
          value={idDocumentStatus}
          label={"Status of ID document"}
          className="col-span-2"
        />
        <DataField
          value={guestStatus}
          label={"Guest status"}
          className="col-span-2"
        />

        {id && (
          <>
            <div className="col-span-2">
              <TransactionHistoryByEntity guestId={id} />
            </div>
          </>
        )}

        {renderTitle("Preferences and notes", "col-span-2 text-left")}
        <DataField
          value={`“${accommodationPreferences}”`}
          label={"Accommodation preferences"}
          className={"col-span-2"}
          editable={true}
          onSave={(v) => {
            if (v === noAccommodationPreferencesLabel) return;

            handleUpdateGuestField({
              field: "preferences",
              value: JSON.stringify({
                ...parsedPreferences,
                accommodation: v,
              }),
            });
          }}
        />

        <div className="col-span-2 flex flex-col gap-3">
          {renderTitle("Guest preferences", "text-lg text-left")}
          {guestPreferences.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {guestPreferences.map((note) => (
                <li
                  key={note}
                  className="border-b-2 border-zinc-900 bg-white py-3 text-sm"
                >
                  {note}
                </li>
              ))}
            </ul>
          ) : (
            <span className="py-3 text-stone-500">
              “No preferences specified”
            </span>
          )}
        </div>

        {!!foodPreferences.length && (
          <div className={"col-span-2 flex flex-col gap-3"}>
            {renderTitle("Food preferences", "col-span-2 text-lg text-left")}
            <div className={"flex flex-row gap-12"}>
              {foodPreferences.map((food: string) => (
                <span key={food}>{food}</span>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-3">
          {renderTitle(
            "Communication Language",
            "col-span-2 text-lg text-left",
          )}
          {prefferedLanguage}
        </div>

        {renderTitle(
          `All guest visits (${guestVisitsCount})`,
          "col-span-2 text-left",
        )}

        <div className="col-span-2 flex flex-col gap-0">
          {guestVisitsCount === 0 ? (
            <div className="py-6 text-stone-500">
              “No visits yet”
              <div className="mt-4 border-b border-zinc-900/60" />
            </div>
          ) : (
            guestVisits?.map((v) => <GuestVisitRow key={v.id} v={v} />)
          )}
        </div>

        {canManageOwnHotelGuest ? (
          <div className="col-span-2 mt-6 flex flex-col gap-4">
            {/* No inFlight signal here: detecting it needs the SUPER_ADMIN-only
                jobs list, which a HOTEL_ADMIN can't read. A second start is
                handled by the 409 (toast + close) in useExportFlow. */}
            <ExportActionCard
              targetType={ExportTargetType.GUEST}
              description={t("dataExport.action.guest.description")}
              buttonLabel={t("dataExport.action.guest.button")}
              onExport={() =>
                exportFlow.startExport({
                  type: ExportTargetType.GUEST,
                  id: guest.id,
                  label: guestConfirmValue,
                })
              }
            />
            <DeletionDangerZone
              targetType={DeletionTargetType.GUEST}
              description={t("dataDeletion.anonymizeGuest.description")}
              buttonLabel={t("dataDeletion.anonymizeGuest.button")}
              onDelete={() => setAnonymizeOpen(true)}
            />
          </div>
        ) : null}
      </div>

      {canManageOwnHotelGuest ? (
        <AnonymizeGuestDialog
          open={anonymizeOpen}
          onOpenChange={setAnonymizeOpen}
          guestId={guest.id}
          confirmValue={guestConfirmValue}
          onCompleted={handleAnonymizeCompleted}
        />
      ) : null}

      {exportFlow.dialogs}
    </div>
  );
};
